import { SOLD_OUT_PLATFORM_DEFAULT } from './deliveryMenuNotes.js'

/**
 * **옵션 품절 토글의 저장 한 번** — 의존성 없는 모듈이다(Vue·`@/` 별칭 금지). 토글은 `components/store/OptStopSellingSwitch.vue`
 * 하나이고 점주·사업장 관리자·관리점·운영사의 「옵션 관리」가 함께 쓴다(R-S).
 */

/** 저장 성공 문구 — 켤 때 · 끌 때 */
const SOLD_OUT_TEXT = '품절 처리되었습니다 (그룹 전체 반영).'
const RELEASED_TEXT = '품절이 해제되었습니다.'
/** 서버가 사유를 싣지 않은 실패 */
const FAILED_TEXT = '품절 처리 실패'

/**
 * 끝난 저장 한 번이 스위치에 반영할 것 — 값(`v-model`)과 안내 한 줄. 순수 함수다.
 *
 * 성공이면 요청한 값, 실패면 그 반대(원래 값)다 — 스위치는 누르는 순간 바뀌지 않으므로 실패의 값은 원복이다.
 *
 * @param {boolean} stopSelling 요청한 값
 * @param {{ok: true, data: *} | {ok: false, error: *}} settled 저장의 끝
 * @param {(key: string) => string} t 번역(`$t`)
 * @returns {{value: boolean, message: Object}} `message` 는 `$message` 의 인자 그대로
 */
export function optStopSellingOutcome(stopSelling, settled, t) {
  if (!settled.ok) {
    const e = settled.error
    return {
      value: !stopSelling,
      message: {
        type: 'error',
        message:
          e && e.response && e.response.data && e.response.data.message ? e.response.data.message : t(FAILED_TEXT),
      },
    }
  }

  // ★★ **배달앱 품절은 그 앱의 기본값만큼만 간다** (규약 §16.243).
  //
  // 그 계약은 「이 문장 없이 「옵션 품절 연동」이라 부르지 않는다」로 기능 명칭의 전제를
  // 걸었는데, 문장이 상수로만 있고 **어느 화면도 렌더하지 않았다**(§16.229). 그러면
  // 사장님은 기한이 지나 옵션이 되살아난 것을 우리 결함으로 읽는다.
  //
  // 품절을 **켤 때만** 말한다 — 해제에는 해당하지 않는 사실이다. 메시지가 길어
  // duration 을 늘린다(기본 3초로는 못 읽는다).
  // ★★ **문장은 사실에서 나온다** (규약 §16.230). §16.229② 는 계약이 기능 명칭의 전제로
  // 건 문장을 띄웠지만 **무조건** 띄웠다 — 그 문장은 「이 품절이 배달앱에 나갔다」와
  // 「내일 아침 풀린다」를 함께 단정하는데, 리얼 활성 매장 1,219곳 중 배달 링크가 있는
  // 곳은 **9곳**이다. 나머지 99.3% 사장님은 존재하지 않는 숙제를 받았다.
  //
  // 판정은 서버가 한다(`deliveryPlatforms`) — 매장 컨텍스트와 capability 표의 사본을
  // 프런트에 만들지 않기 위해서다(§16.214).
  const platforms = (settled.data && settled.data.deliveryPlatforms) || []
  return {
    value: stopSelling,
    message:
      stopSelling && platforms.length > 0
        ? {
            type: 'success',
            duration: 8000,
            showClose: true,
            message: t(SOLD_OUT_TEXT) + ' ' + t(SOLD_OUT_PLATFORM_DEFAULT),
          }
        : { type: 'success', message: stopSelling ? t(SOLD_OUT_TEXT) : t(RELEASED_TEXT) },
  }
}

/**
 * 누른 값이 **나가 있는 저장에 합쳐지는가** — 순수 함수다.
 *
 * 합친다 = 이 토글의 진행 중 저장이 있고, 그 저장이 아직 이 토글의 회차이며(옵션이 그대로 · 토글이 살아 있음), 목표값이 같다.
 * 스위치는 응답 뒤에만 바뀌므로 응답 전에 다시 누르면(포커스된 스위치의 Enter 연타) 늘 같은 목표값이다. 그것을 따로 보내면
 * 뒤의 요청이 먼저 실패해 원복하고 앞의 늦은 성공은 낡은 회차가 되어 버려졌다 — 서버는 품절인데 화면은 원복(Codex 리뷰).
 * 합치면 한 옵션에 나가 있는 저장은 늘 하나이고 결과·안내도 하나다. 기다리는 동안 스위치를 잠그거나 흐리게 하지 않는다 —
 * 다시 누름은 조용히 합쳐진다.
 *
 * 옵션이 바뀐 뒤(`invalidate`)의 같은 값 누름은 **다른 옵션의 저장**이라 합치지 않는다(앞 옵션의 저장은 낡은 회차다).
 *
 * @param {{token: number, stopSelling: boolean, done: Promise<void>} | null} inFlight 이 토글의 진행 중 저장
 * @param {boolean} stopSelling 누른 값
 * @param {{isLatest: (token: number) => boolean}} guard 토글의 가드
 * @returns {boolean}
 */
export function joinsInFlight(inFlight, stopSelling, guard) {
  return !!inFlight && inFlight.stopSelling === stopSelling && guard.isLatest(inFlight.token)
}

/**
 * **저장 한 번의 수명** — 요청을 보내고, 끝났을 때 그 요청이 **아직 이 토글의 회차**일 때만 반영할 것을 `apply` 로 넘긴다.
 * 같은 목표값의 저장이 나가 있으면 새 요청을 보내지 않고 그 저장에 합친다([joinsInFlight]) — 그 저장의 끝을 함께 기다린다.
 *
 * 토글은 자기 옵션을 고르지 않는다 — 행 키가 없는 표(`el-table`)는 매장·페이지가 바뀌면 같은 줄의 토글을 **다른 옵션**에 다시
 * 쓴다. 응답의 수명을 모르던 동안 늦게 온 성공이 `input` 으로 그 사이 들어온 다른 매장 옵션을 품절로 그렸고(실패의 원복도 같다),
 * 줄이 사라진 토글은 아무도 보지 않는 안내를 띄웠다. 그래서 회차는 토글의 가드(`utils/latestGuard`)가 정한다 — 옵션 코드가
 * 바뀌면 `invalidate`, 토글이 사라지면 `close`. 가드의 일은 그것뿐이다: 같은 옵션의 다시 누름은 합쳐지므로 새 요청이 회차를
 * 넘기는 것은 목표값이 다를 때(그 사이 표가 서버 값을 다시 읽어 스위치가 바뀐 드문 경우)뿐이고, 그때는 나중 뜻이 화면을 정한다.
 *
 * 진행 중 저장은 **토글 인스턴스가 갖는다**(`inFlight` 칸 — 읽기·쓰기를 부르는 쪽이 준다). 모듈 전역이 아니다: 토글마다 따로다.
 *
 * **응답을 버린다고 서버 저장이 취소되지는 않는다** — 요청은 이미 나갔고 서버에서 끝난다. 버림은 그 결과를 이 토글이 지금
 * 그리는 옵션에 쓰지 않는다는 뜻일 뿐이다(떠난 옵션의 품절 여부는 그 매장을 다시 읽으면 서버 값으로 보인다).
 *
 * @param {{guard: {begin: () => number, isLatest: (token: number) => boolean},
 *   inFlight: {read: () => (Object|null), write: (entry: (Object|null)) => void}, send: () => Promise<{data?: *}>,
 *   t: (key: string) => string, apply: (outcome: Object) => void}} deps
 *   `guard` 토글의 가드 · `inFlight` 토글의 진행 중 저장 칸 · `send` 저장 요청(본문은 부르는 쪽이 만든다) · `t` 번역 ·
 *   `apply` 반영(판정과 같은 동기 구간에서 불린다)
 * @param {boolean} stopSelling 누른 값
 * @returns {Promise<void>} 이 저장(합쳐졌으면 나가 있던 저장)의 끝
 */
export function saveOptStopSelling(deps, stopSelling) {
  const { guard, inFlight } = deps
  const pending = inFlight.read()
  if (joinsInFlight(pending, stopSelling, guard)) return pending.done // 같은 저장이다 — 새 요청도, 두 번째 결과·안내도 없다

  const token = guard.begin()
  const done = deps
    .send()
    .then(
      (res) => ({ ok: true, data: res && res.data }),
      (error) => ({ ok: false, error })
    )
    .then((settled) => {
      // 끝났다 — 다음 누름은 새 저장이다(칸이 아직 이 저장의 것일 때만 비운다)
      const current = inFlight.read()
      if (current && current.token === token) inFlight.write(null)

      if (!guard.isLatest(token)) return // 다른 옵션으로 바뀌었거나 토글이 사라졌다 — 이 응답은 버린다
      deps.apply(optStopSellingOutcome(stopSelling, settled, deps.t))
    })

  inFlight.write(Object.freeze({ token, stopSelling, done }))
  return done
}
